import { CASE_DOCUMENT_CATEGORIES, CASE_DOCUMENT_CATEGORY_LABELS, FIELD_DOCUMENT_CATEGORIES, type CaseDocumentCategory, type CaseDocumentDto, type CaseDto, type CaseFieldPhotoDto, datePl, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES, DOCUMENT_TYPES, documentExtension, type RequiredDocumentDto, TASK_TYPES } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, FileButton, Group, Image, Modal, Paper, Select, SimpleGrid, Stack, Table, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Link } from 'react-router';
import { formatDateTime, plural } from '../../format';
import { IconCircleCheckFilled, IconCircleXFilled, IconDeviceMobile, IconDownload, IconFileText, IconPaperclip, IconPencil, IconTrash, IconUpload } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type DragEvent, useState } from 'react';
import { api, errorText } from '../../api';
import { ask } from '../../confirm';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { type GalleryPhoto, useGallery } from '../../components/PhotoGallery';
import { CaseCorrespondence } from '../party/PartyCorrespondence';

const size = (b: number) => (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} kB`);

/** Podpowiedź rodzaju po nazwie pliku. */
function guessCategory(name: string): CaseDocumentCategory {
  const n = name.toLowerCase();
  if (/projekt|rzut|schemat|\.dwg$|\.dxf$/.test(n)) return 'projekt';
  if (/dotacj|wniosek|nfo|wfos|czyste|m[oó]j.?pr[aą]d/.test(n)) return 'dotacja';
  if (/\.(jpe?g|png|webp|heic)$/.test(n)) return 'zdjecia';
  if (/\.eml$|mail|pismo/.test(n)) return 'korespondencja';
  if (/karta|instrukcj|dtr|certyfikat|atest/.test(n)) return 'dokumentacja';
  return 'inne';
}

const CATEGORY_OPTIONS = CASE_DOCUMENT_CATEGORIES.map((k) => ({ value: k, label: `${CASE_DOCUMENT_CATEGORY_LABELS[k]}${FIELD_DOCUMENT_CATEGORIES.includes(k) ? ' — widzi ekipa' : ''}` }));
const FIELD_HINT = 'Projekt, dokumentację techniczną, zdjęcia i inne widzi ekipa w aplikacji mobilnej (przy pracach w tej sprawie); korespondencji i dotacji — nie.';

/** Plik wybrany do dodania: rodzaj podpowiedziany z nazwy, opis; plik nieobsługiwany — z powodem (nie zostanie wysłany). */
interface Pending {
  file: File;
  category: CaseDocumentCategory;
  description: string;
  error: string | null;
}
const pendingOf = (file: File): Pending => ({
  file,
  category: guessCategory(file.name),
  description: '',
  error: !DOCUMENT_TYPES[documentExtension(file.name)] ? 'nieobsługiwany typ pliku' : file.size > DOCUMENT_MAX_BYTES ? 'większy niż 50 MB' : null,
});

async function upload(caseId: string, file: File, category: CaseDocumentCategory, description: string) {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  form.append('description', description);
  const res = await fetch(`/api/cases/${caseId}/documents`, { method: 'POST', body: form, credentials: 'same-origin' });
  if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { message?: string }).message ?? `Nie udało się dodać pliku ${file.name}`);
  return (await res.json()) as CaseDocumentDto;
}

/** Okno po wybraniu plików: rodzaj i opis każdego pliku, potem jedno „Dodaj”. */
function AddDocumentsModal({ pending, onChange, busy, onSubmit, onClose }: { pending: Pending[]; onChange: (p: Pending[]) => void; busy: boolean; onSubmit: () => void; onClose: () => void }) {
  const phone = useMediaQuery('(max-width: 48em)');
  const set = (i: number, patch: Partial<Pending>) => onChange(pending.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const valid = pending.filter((p) => !p.error).length;
  return (
    <Modal opened onClose={onClose} title={pending.length === 1 ? 'Dodaj dokument' : `Dodaj dokumenty (${pending.length})`} size="lg" fullScreen={phone}>
      <Stack gap="md">
        {pending.map((p, i) => (
          <Paper key={`${p.file.name}-${i}`} withBorder p="sm" radius="md">
            <Group gap="xs" wrap="nowrap" mb={p.error ? 0 : 'xs'}>
              <IconFileText size={18} style={{ flexShrink: 0, opacity: 0.6 }} />
              <Text size="sm" fw={600} style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                {p.file.name}
              </Text>
              <Text size="xs" c="dimmed">
                {size(p.file.size)}
              </Text>
            </Group>
            {p.error ? (
              <Text size="xs" c="red">
                Nie zostanie dodany: {p.error}
              </Text>
            ) : (
              <Group gap="sm" grow align="flex-start">
                <Select label="Rodzaj" size="xs" allowDeselect={false} value={p.category} onChange={(v) => v && set(i, { category: v as CaseDocumentCategory })} data={CATEGORY_OPTIONS} />
                <TextInput label="Opis (opcjonalnie)" size="xs" placeholder="np. projekt instalacji od klienta" value={p.description} maxLength={500} onChange={(e) => set(i, { description: e.currentTarget.value })} />
              </Group>
            )}
          </Paper>
        ))}
        <Text size="xs" c="dimmed">
          {FIELD_HINT}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Anuluj
          </Button>
          <Button onClick={onSubmit} loading={busy} disabled={!valid}>
            {valid === 1 ? 'Dodaj' : `Dodaj (${valid})`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Zmiana rodzaju i opisu dodanego dokumentu (plik zostaje). */
function EditDocumentModal({ doc, onClose }: { doc: CaseDocumentDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [category, setCategory] = useState<CaseDocumentCategory>(doc.category);
  const [description, setDescription] = useState(doc.description);
  const save = useMutation({
    mutationFn: () => api(`/case-documents/${doc.id}`, { method: 'PATCH', body: { category, description: description.trim() } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['case-documents', doc.caseId] });
      notifyOk('Zapisano dokument');
      onClose();
    },
    onError: notifyError,
  });
  const fieldNow = FIELD_DOCUMENT_CATEGORIES.includes(category);
  return (
    <Modal opened onClose={onClose} title={doc.fileName}>
      <Stack gap="sm">
        <Select label="Rodzaj" allowDeselect={false} value={category} onChange={(v) => v && setCategory(v as CaseDocumentCategory)} data={CATEGORY_OPTIONS} data-autofocus />
        <TextInput label="Opis (opcjonalnie)" value={description} maxLength={500} onChange={(e) => setDescription(e.currentTarget.value)} />
        <Alert color={fieldNow ? 'eko' : 'gray'} variant="light" icon={<IconDeviceMobile size={16} />}>
          <Text size="xs">{fieldNow ? 'Ekipa zobaczy ten dokument w aplikacji mobilnej przy pracach w tej sprawie (po synchronizacji).' : 'Ekipa nie zobaczy tego dokumentu w aplikacji mobilnej.'}</Text>
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Zakładka „Dokumenty”: pliki dołączone do sprawy (projekt od klienta, dokumentacja, korespondencja). */
export function DocumentsTab({ c }: { c: CaseDto }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['case-documents', c.id], queryFn: () => api<CaseDocumentDto[]>(`/cases/${c.id}/documents`) });
  // jeden proces: wybór plików (albo przeciągnięcie) → okno z rodzajem i opisem każdego pliku → „Dodaj”
  const [pending, setPending] = useState<Pending[] | null>(null);
  const [editing, setEditing] = useState<CaseDocumentDto | null>(null);
  const [drag, setDrag] = useState(false);

  const add = useMutation({
    // najpierw sprawdzamy wszystkie pliki; złe pomijamy, dobre wgrywamy — na końcu jasna lista, co się udało, a co nie
    mutationFn: async (list: Pending[]) => {
      const ok: string[] = [];
      const failed: string[] = [];
      for (const p of list) {
        if (p.error) continue;
        try {
          await upload(c.id, p.file, p.category, p.description.trim());
          ok.push(p.file.name);
        } catch (e) {
          failed.push(`${p.file.name} — ${errorText(e)}`);
        }
      }
      return { ok, failed };
    },
    onSuccess: ({ ok, failed }) => {
      if (ok.length) notifyOk(ok.length === 1 ? `Dodano: ${ok[0]}` : `Dodano plików: ${ok.length}`);
      if (failed.length) notifyError(new Error([ok.length ? 'Nie dodano części plików:' : 'Nie dodano plików:', ...failed].join('\n')));
      setPending(null);
    },
    onError: notifyError,
    onSettled: () => void qc.invalidateQueries({ queryKey: ['case-documents', c.id] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/case-documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['case-documents', c.id] });
      notifyOk('Usunięto dokument');
    },
    onError: notifyError,
  });

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const list = Array.from(e.dataTransfer.files);
    if (list.length && c.can.documents) setPending(list.map(pendingOf));
  };

  const docs = q.data ?? [];
  // zdjęcia wgrane jako dokumenty — kliknięcie nazwy otwiera galerię wszystkich zdjęć z listy
  const docGallery = useGallery(
    docs
      .filter((d) => d.mime.startsWith('image/') && d.inline)
      .map((d) => ({ id: d.id, src: `/api/case-documents/${d.id}/file`, caption: `${d.fileName} · ${d.createdByName ?? '—'} · ${datePl(d.createdAt)}`, detail: d.description || null, fileName: d.fileName })),
  );
  return (
    <Stack gap="md">
      <RequiredDocuments caseId={c.id} />
      {/* korespondencja z klientem przypięta do sprawy (docs/20) */}
      <CaseCorrespondence caseId={c.id} clientId={c.client.id} />
      {c.can.documents && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{ borderStyle: 'dashed', borderWidth: 2, borderColor: drag ? 'var(--mantine-color-eko-6)' : undefined, background: drag ? 'var(--app-highlight)' : undefined }}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
        >
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap">
              <IconUpload size={22} style={{ flexShrink: 0, opacity: 0.6 }} />
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={600}>
                  Przeciągnij pliki tutaj albo wybierz z dysku
                </Text>
                <Text size="xs" c="dimmed">
                  PDF, zdjęcia, Word, Excel, OpenDocument, DWG/DXF, ZIP, TXT/CSV, EML — do 50 MB
                </Text>
              </div>
              <FileButton onChange={(f) => f.length && setPending(f.map(pendingOf))} accept={DOCUMENT_ACCEPT} multiple>
                {(props) => (
                  <Button {...props} leftSection={<IconPaperclip size={16} />} loading={add.isPending}>
                    Dodaj pliki
                  </Button>
                )}
              </FileButton>
            </Group>
          </Stack>
        </Paper>
      )}
      {pending && <AddDocumentsModal pending={pending} onChange={setPending} busy={add.isPending} onSubmit={() => add.mutate(pending)} onClose={() => !add.isPending && setPending(null)} />}
      {editing && <EditDocumentModal doc={editing} onClose={() => setEditing(null)} />}
      {docGallery.gallery}

      <QueryState isLoading={q.isLoading} error={q.error} lines={3}>
      {docs.length === 0 ? (
        <EmptyState compact title="Brak dokumentów w sprawie" hint={c.can.documents ? 'Dodaj projekt, dokumentację albo korespondencję od klienta.' : undefined} />
      ) : (
        <Table.ScrollContainer minWidth={560}>
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Plik</Table.Th>
                <Table.Th>Rodzaj</Table.Th>
                <Table.Th>Dodano</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {docs.map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap" align="flex-start">
                      <IconFileText size={18} style={{ flexShrink: 0, marginTop: 2, opacity: 0.6 }} />
                      <div style={{ minWidth: 0 }}>
                        <Anchor
                          href={`/api/case-documents/${d.id}/file`}
                          target={d.inline ? '_blank' : undefined}
                          rel="noopener"
                          size="sm"
                          fw={600}
                          style={{ wordBreak: 'break-word' }}
                          onClick={(e) => {
                            if (d.mime.startsWith('image/') && d.inline) {
                              e.preventDefault();
                              docGallery.open(d.id);
                            }
                          }}
                        >
                          {d.fileName}
                        </Anchor>
                        <Text size="xs" c="dimmed">
                          {size(d.size)}
                          {d.description ? ` · ${d.description}` : ''}
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Badge variant="light" color="gray" size="sm" style={d.canDelete ? { cursor: 'pointer' } : undefined} onClick={d.canDelete ? () => setEditing(d) : undefined} title={d.canDelete ? 'Zmień rodzaj' : undefined}>
                        {CASE_DOCUMENT_CATEGORY_LABELS[d.category]}
                      </Badge>
                      {FIELD_DOCUMENT_CATEGORIES.includes(d.category) && (
                        <Tooltip label="Widoczny dla ekipy w aplikacji mobilnej (przy pracach w tej sprawie)">
                          <IconDeviceMobile size={14} color="var(--mantine-color-dimmed)" aria-label="widzi ekipa" />
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{datePl(d.createdAt)}</Text>
                    <Text size="xs" c="dimmed">
                      {d.createdByName ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap" justify="flex-end">
                      <Tooltip label="Pobierz">
                        <ActionIcon variant="subtle" color="gray" component="a" href={`/api/case-documents/${d.id}/file`} download={d.fileName} aria-label={`Pobierz ${d.fileName}`}>
                          <IconDownload size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {d.canDelete && (
                        <Tooltip label="Zmień rodzaj i opis">
                          <ActionIcon variant="subtle" color="gray" aria-label={`Zmień rodzaj i opis: ${d.fileName}`} onClick={() => setEditing(d)}>
                            <IconPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {d.canDelete && (
                        <Tooltip label="Usuń ze sprawy">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Usuń ${d.fileName}`}
                            loading={remove.isPending && remove.variables === d.id}
                            onClick={async () => {
                              if (await ask(`Usunąć „${d.fileName}” ze sprawy? Wpis zostanie w historii sprawy.`, { title: 'Usunąć dokument?', confirm: 'Usuń', danger: true })) remove.mutate(d.id);
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      </QueryState>
      <FieldPhotos caseId={c.id} />
    </Stack>
  );
}

/**
 * Zdjęcia z prac w terenie (wizja, pomiary, realizacja, poprawka, serwis — z czynności i ogólne), pogrupowane
 * po zadaniach. Przy każdym źródło: zadanie (rodzaj, numer, termin), autor, czas, czynność z listy.
 */
function FieldPhotos({ caseId }: { caseId: string }) {
  const q = useQuery({ queryKey: ['case-field-photos', caseId], queryFn: () => api<CaseFieldPhotoDto[]>(`/cases/${caseId}/field-photos`) });
  const groups: { taskId: string; head: CaseFieldPhotoDto; items: CaseFieldPhotoDto[] }[] = [];
  for (const p of q.data ?? []) {
    const g = groups.find((x) => x.taskId === p.taskId);
    if (g) g.items.push(p);
    else groups.push({ taskId: p.taskId, head: p, items: [p] });
  }
  // galeria: wszystkie wysłane zdjęcia sprawy w kolejności widoku (przechodzi między pracami)
  const gallery = useGallery(
    groups.flatMap(({ head, items }) =>
      items
        .filter((p) => p.uploaded)
        .map(
          (p): GalleryPhoto => ({
            id: p.id,
            src: `/api/case-field-photos/${p.id}/file`,
            caption: `${TASK_TYPES[head.taskType]?.label ?? 'Praca'} ${head.taskNumber} · ${p.authorName} · ${formatDateTime(p.takenAt)}`,
            detail: p.checklistLabel,
            fileName: `zdjecie-${head.taskNumber.replace(/\W+/g, '-')}.jpg`,
          }),
        ),
    ),
  );
  return (
    <Stack gap="xs">
      <Text fw={600}>Zdjęcia z prac{q.data?.length ? ` (${q.data.length})` : ''}</Text>
      <QueryState isLoading={q.isLoading} error={q.error} lines={2}>
        {groups.length === 0 ? (
          <EmptyState compact title="Brak zdjęć z prac" hint="Zdjęcia z wizji, pomiarów, realizacji i serwisu pojawią się tu po synchronizacji aplikacji mobilnej." />
        ) : (
          groups.map(({ taskId, head, items }) => (
            <Paper key={taskId} withBorder p="sm" radius="md">
              <Group gap="xs" mb={8} wrap="wrap">
                <Badge variant="light" color="gray" size="sm">
                  {TASK_TYPES[head.taskType]?.label ?? 'Zadanie'}
                </Badge>
                <Anchor component={Link} to={`/sprawy/${caseId}?zakladka=prace&zadanie=${taskId}`} size="sm" fw={600}>
                  {head.taskNumber}
                </Anchor>
                <Text size="sm" c="dimmed">
                  {head.taskTitle && head.taskTitle !== TASK_TYPES[head.taskType]?.label ? `${head.taskTitle} · ` : ''}
                  {datePl(head.taskDate)} · {items.length} {plural(items.length, 'zdjęcie', 'zdjęcia', 'zdjęć')}
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 2, xs: 3, sm: 4, md: 6 }} spacing="xs">
                {items.map((p) => (
                  <Stack key={p.id} gap={2}>
                    {p.uploaded ? (
                      <UnstyledButton onClick={() => gallery.open(p.id)} aria-label={`Otwórz galerię: zdjęcie z ${head.taskNumber} — ${p.authorName}`} style={{ display: 'block', cursor: 'zoom-in' }}>
                        <Image src={`/api/case-field-photos/${p.id}/file`} h={110} fit="cover" radius="sm" loading="lazy" alt={`Zdjęcie z ${head.taskNumber} — ${p.authorName}`} />
                      </UnstyledButton>
                    ) : (
                      <Paper h={110} radius="sm" bg="var(--mantine-color-default-hover)" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Text size="xs" c="dimmed">
                          plik w drodze
                        </Text>
                      </Paper>
                    )}
                    <Text size="xs" lh={1.3}>
                      {p.authorName} · {formatDateTime(p.takenAt)}
                    </Text>
                    {p.checklistLabel && (
                      <Text size="xs" c="dimmed" lh={1.3} lineClamp={2}>
                        {p.checklistLabel}
                      </Text>
                    )}
                  </Stack>
                ))}
              </SimpleGrid>
            </Paper>
          ))
        )}
      </QueryState>
      {gallery.gallery}
    </Stack>
  );
}

/** Wymagane dokumenty sprawy (z ustawień branż) — co jest, czego brakuje przed zamknięciem (docs/10 §3.7). */
function RequiredDocuments({ caseId }: { caseId: string }) {
  const q = useQuery({ queryKey: ['case-required-documents', caseId], queryFn: () => api<RequiredDocumentDto[]>(`/cases/${caseId}/required-documents`) });
  if (!q.data?.length) return null;
  return (
    <Paper withBorder p="sm" radius="md">
      <Text size="sm" fw={600} mb={6}>
        Wymagane dokumenty
      </Text>
      <Group gap="xs" wrap="wrap">
        {q.data.map((d) => (
          <Badge key={d.category} variant="light" color={d.ok ? 'green' : 'orange'} title={`wymaga: ${d.trades.join(', ')}`} leftSection={d.ok ? <IconCircleCheckFilled size={12} /> : <IconCircleXFilled size={12} />}>
            {d.label}
            {d.ok ? ` (${d.count})` : ''}
          </Badge>
        ))}
      </Group>
    </Paper>
  );
}
